import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import styled from "styled-components";
import moment from "moment";

import { getAllProperties, clearUploadErrors } from "../../store/state/properties";
import PropertiesHeader from "./PropertiesHeader";
import PropertiesList from "./PropertiesList";

const DESC_ORDER = "desc";
const ASC_ORDER = "asc";

const ALL_PROPERTIES_OPTION = {
  id: -1,
  label: "All properties",
  name: "All properties",
  value: -1
};

const NO_ASSET_MANAGER_ALLOCATED_PROPERTY_OPTION = {
  id: -2,
  label: "N/A",
  name: "N/A",  
  value: -2
};

const PagePadding = styled.div`
  padding: 16px;
  max-width:90%;
  margin: auto;
`;

class PropertiesComponent extends Component {
  // sorts the records
  static sortRecords(direction, property, records) {
    const { isString, isDateField, dataField } = property;

    // sort the records
    const sortedRecords = records.sort((a, b) => {
      if (isString) {
        // for string ignore case
        const aValue = a[dataField] ? a[dataField].toLowerCase() : "";
        const bValue = b[dataField] ? b[dataField].toLowerCase() : "";

        // based on direction
        if (aValue < bValue) {
          return direction === DESC_ORDER ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === DESC_ORDER ? 1 : -1;
        }

        // strings must be equal
        return 0;
      } 
      if (isDateField) {
        // for dates
        const aValue = a[dataField] ? moment(a[dataField]) : moment();
        const bValue = b[dataField] ? moment(b[dataField]) : moment();

        // based on direction
        if (aValue.isBefore(bValue)) {
          return direction === DESC_ORDER ? -1 : 1;
        }
        if (aValue.isAfter(bValue)) {
          return direction === DESC_ORDER ? 1 : -1;
        }

        // strings must be equal
        return 0;
      }
      // number comparison, or if dataType is not known
      const aValue = a[dataField] ? a[dataField] : 0;
      const bValue = b[dataField] ? b[dataField] : 0;

      return direction === DESC_ORDER ? aValue - bValue : bValue - aValue;
    });

    return sortedRecords;
  }

  // default onload sorting
  static sortDefault(records) {
    const dealProperty = {
      isString: true,
      isDateField: false,
      isBaseColumn: true,
      dataField: "dealName"
    };

    // sort the records before passing it to props
    const sortedRecords = PropertiesComponent.sortRecords(DESC_ORDER, dealProperty, records);
    return sortedRecords;
  }

  constructor(props) {
    super(props);

    this.state = {
      orderDirection: DESC_ORDER,
      orderBy: "dealName",
      assetManager: -1,
      searchName: "",
      records: [],
      recordsAvailable: false
    };
  }

  componentDidMount() {
    // do the get call
    this.props.getAllProperties();
  }

  static getDerivedStateFromProps(props, state) {
    if (props.uploadSuccessful || props.recordsAvailable !== state.recordsAvailable || props.records !== state.originalRecords) {
      const records = props.uploadSuccessful ? PropertiesComponent.filterRecords(props.records, state.assetManager, state.searchName) : props.records;
      return {
        recordsAvailable: props.recordsAvailable,
        records,
        originalRecords: props.records
      };
    }
    return null;
  }

  // filters the records
  static filterRecords(records, assetManager, searchName) {
    // filter the matching records from all
    const filteredRecords = records.filter(record => {
      // first filter by asset manager
      let assetManagerFilter = false;
      if (assetManager === -1) {
        // for all asset manager results
        assetManagerFilter = true;
      } else if (assetManager === -2) {
        // for records where asset manager is null
        assetManagerFilter = record.assetManagerId === null;
      } else {
        assetManagerFilter = record.assetManagerId === assetManager;
      }

      // asset manager search passed, now look for name
      if (assetManagerFilter) {
        const searchValue = searchName ? searchName.toLowerCase() : "";
        const dealNameFilter = record.dealName ? record.dealName.toLowerCase().indexOf(searchValue) > -1 : false;
        const propertyFilter = record.property ? record.property.toLowerCase().indexOf(searchValue) > -1 : false;
        return dealNameFilter || propertyFilter;
      }
      return false;
    });

    return filteredRecords;
  }

  // handles the filter fields change events
  handleFilterFieldsChange = (name, value) => {
    const { assetManager, searchName } = this.state;
    // pass the existing value, and update the recently changed value
    const filterObject = {
      assetManager,
      searchName,
      [name]: value
    };
    // filter the records
    const filteredRecords = PropertiesComponent.filterRecords(this.state.originalRecords, filterObject.assetManager, filterObject.searchName);
    // update the state
    this.setState({
      ...filterObject,
      records: filteredRecords
    });
  };

  // handles the sort requests
  handleSortRecords = (event, property) => {
    const { orderDirection, orderBy, records } = this.state;
    const { dataField } = property;

    // defines the direction
    let orderDir = DESC_ORDER;
    if (orderBy === dataField && orderDirection === DESC_ORDER) {
      orderDir = ASC_ORDER;
    }

    // sort the fields
    const sortedRecords = PropertiesComponent.sortRecords(orderDir, property, records);

    // update the state
    this.setState({
      orderDirection: orderDir,
      orderBy: dataField,
      records: sortedRecords
    });
  };

  render() {
    const { assetManager, searchName, records, orderDirection, orderBy } = this.state;
    const { assetManagerList } = this.props;

    const availableAssetManagers =
      assetManagerList && Array.isArray(assetManagerList)
        ? [ALL_PROPERTIES_OPTION, ...assetManagerList, NO_ASSET_MANAGER_ALLOCATED_PROPERTY_OPTION]
        : [ALL_PROPERTIES_OPTION, NO_ASSET_MANAGER_ALLOCATED_PROPERTY_OPTION];

    return (
      <>
        <PagePadding>
          {/* The header */}
          <PropertiesHeader
            records={records}
            searchName={searchName}
            assetManager={assetManager}
            assetManagerList={availableAssetManagers}
            handleChange={this.handleFilterFieldsChange}
          />
          {/* The table */}
          <PropertiesList records={records} orderDirection={orderDirection} orderBy={orderBy} handleRequestSort={this.handleSortRecords} />
        </PagePadding>
      </>
    );
  }
}

const mapStateToProps = ({ properties }) => ({
  recordsAvailable: properties.recordsAvailable,
  records: properties.allProperties ? PropertiesComponent.sortDefault(properties.allProperties) : [],
  assetManagerList: properties.assetManagers,
  uploadSuccessful: properties.uploadSuccessful
});

PropertiesComponent.propTypes = {
  getAllProperties: PropTypes.func.isRequired,
  clearUploadErrors: PropTypes.func.isRequired,
  records: PropTypes.array.isRequired,
  assetManagerList: PropTypes.array.isRequired,
  recordsAvailable: PropTypes.bool.isRequired
};

export default connect(mapStateToProps, { getAllProperties, clearUploadErrors })(PropertiesComponent);
